import { GenerationResourceType } from '@/types/generation-reference';
import type { CanvasItemType, CanvasNodeCreateRequest } from '@/types/canvas';

export const MARKUP_REFERENCE_ATTACHMENT_RELATED_TYPE = 'project' as const;
export const MARKUP_REFERENCE_ATTACHMENT_PURPOSE = 'attachment' as const;

export function getMarkupReferenceAttachmentRelatedParams(projectId: string): {
  relatedId: string;
  relatedType: typeof MARKUP_REFERENCE_ATTACHMENT_RELATED_TYPE;
  attachmentPurpose: typeof MARKUP_REFERENCE_ATTACHMENT_PURPOSE;
} {
  return {
    relatedId: projectId,
    relatedType: MARKUP_REFERENCE_ATTACHMENT_RELATED_TYPE,
    attachmentPurpose: MARKUP_REFERENCE_ATTACHMENT_PURPOSE,
  };
}

/** 附件 pin 身份：列类型优先；缺类型时认 sourceKind=attachment 或旧 markupPin。 */
export function isCanvasPinAttachmentIdentity(data: {
  sourceResourceType?: unknown;
  sourceKind?: unknown;
  markupPin?: unknown;
}): boolean {
  const explicit =
    typeof data.sourceResourceType === 'number' && Number.isFinite(data.sourceResourceType)
      ? data.sourceResourceType
      : typeof data.sourceResourceType === 'string' && data.sourceResourceType.trim()
        ? Number(data.sourceResourceType)
        : Number.NaN;
  if (
    explicit === GenerationResourceType.PROJECT_RESOURCE ||
    explicit === GenerationResourceType.STORYBOARD_IMAGE ||
    explicit === GenerationResourceType.STORYBOARD_VIDEO ||
    explicit === GenerationResourceType.ATTACHMENT ||
    explicit === GenerationResourceType.SOUND_EFFECT ||
    explicit === GenerationResourceType.VOICE_OVER ||
    explicit === GenerationResourceType.HUMAN_ASSET
  ) {
    return explicit === GenerationResourceType.ATTACHMENT;
  }
  return data.sourceKind === 'attachment' || data.markupPin === true;
}

export function isUploadedAttachmentBoardRelated(params: {
  relatedType?: string | null;
  attachmentPurpose?: string | null;
}): boolean {
  return params.relatedType === 'storyboard' && params.attachmentPurpose === 'reference';
}

export function resolveMarkupReferencePinSourceHint(
  node:
    | {
        type?: string;
        data?: unknown;
      }
    | null
    | undefined
): string | undefined {
  if (!node || node.data == null || typeof node.data !== 'object') return undefined;
  const data = node.data as Record<string, unknown>;
  if (node.type === 'storyboardItemMedia') {
    const resourceId = data.resourceId;
    return typeof resourceId === 'string' && resourceId.trim() ? resourceId : undefined;
  }
  if (node.type === 'storyboardReferenceImage') {
    const sourceResourceId = data.sourceResourceId;
    return typeof sourceResourceId === 'string' && sourceResourceId.trim()
      ? sourceResourceId
      : undefined;
  }
  if (node.type === 'canvasReferenceProjection') {
    const projection = data.projection as
      | { historyItem?: { resource?: { resourceId?: string | null } } }
      | undefined;
    const resourceId = projection?.historyItem?.resource?.resourceId?.trim();
    return resourceId || undefined;
  }
  return undefined;
}

export function requireMarkupReferencePinSource<T extends { id: string }>(params: {
  storyboardId?: string | null;
  sourceNode?: T;
  sourceResourceId?: string | null;
}): { storyboardId: string; sourceNode: T; sourceResourceId: string } {
  if (!params.storyboardId) {
    throw new Error('当前画板无法钉参考图');
  }
  if (!params.sourceNode) {
    throw new Error('未能定位源节点以钉到画布');
  }
  if (!params.sourceResourceId) {
    throw new Error('源节点缺少资源 ID，无法建立参考图连线');
  }
  return {
    storyboardId: params.storyboardId,
    sourceNode: params.sourceNode,
    sourceResourceId: params.sourceResourceId,
  };
}

export interface BuildMarkupReferencePinCreatePayloadInput {
  storyboardId: string;
  storyboardTitle?: string;
  attachmentId: string;
  fileUrl: string;
  title: string;
  width?: number | null;
  height?: number | null;
  mimeType?: string | null;
  sourcePlanId?: string;
  sourcePlanName?: string;
  sourceResourceId: string;
  sourceResourceType: number;
  sourceLayoutItemId?: string;
  editAction?: 'crop' | 'compress' | 'markup' | 'erase' | 'repaint';
  position: { x: number; y: number; zIndex: number };
}

export function buildMarkupReferencePinCreatePayload(
  input: BuildMarkupReferencePinCreatePayloadInput
): Pick<
  CanvasNodeCreateRequest,
  | 'itemType'
  | 'sourceResourceType'
  | 'sourceResourceId'
  | 'x'
  | 'y'
  | 'zIndex'
  | 'metaData'
  | 'storyboardId'
> {
  const itemType: CanvasItemType = 'resource_pin';

  const title = input.title.trim() || '参考图';
  const planName = input.sourcePlanName?.trim() || title;

  return {
    storyboardId: input.storyboardId,
    itemType,
    sourceResourceType: GenerationResourceType.ATTACHMENT,
    sourceResourceId: input.attachmentId,
    x: input.position.x,
    y: input.position.y,
    zIndex: input.position.zIndex,
    metaData: {
      mediaType: 'image',
      sourceKind: 'attachment',
      sourceId: input.sourcePlanId ?? input.attachmentId,
      resourceId: input.attachmentId,
      url: input.fileUrl,
      storyboardId: input.storyboardId,
      storyboardTitle: input.storyboardTitle?.trim() || title,
      ...(input.sourcePlanId ? { planId: input.sourcePlanId } : {}),
      planName,
      imageUrl: input.fileUrl,
      thumbnailUrl: input.fileUrl,
      width: input.width ?? undefined,
      height: input.height ?? undefined,
      ...(input.mimeType ? { mimeType: input.mimeType } : {}),
      title,
      sourceLabel: '参考图',
      subtitle: planName,
      editSourceResourceId: input.sourceResourceId,
      editSourceResourceType: input.sourceResourceType,
      editSourceEditAction: input.editAction ?? 'markup',
      ...(input.sourceLayoutItemId?.trim()
        ? { editSourceLayoutItemId: input.sourceLayoutItemId.trim() }
        : {}),
    },
  };
}
